import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  analyzeBankStatements,
  type AnalyzeResult,
} from "@/lib/bankStatements.functions";

export const Route = createFileRoute("/tools/bank-statements")({
  head: () => ({
    meta: [
      { title: "Bank Statement Analyzer | Tools" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BankStatementsTool,
});

type PickedFile = { file: File; id: string };

function BankStatementsTool() {
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalyzeResult | null>(null);

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next: PickedFile[] = [];
    for (const f of Array.from(list)) {
      next.push({ file: f, id: `${f.name}-${f.size}-${crypto.randomUUID()}` });
    }
    setFiles((prev) => [...prev, ...next].slice(0, 12));
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  async function toBase64(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
  }

  async function analyze() {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const payloadFiles = await Promise.all(
        files.map(async ({ file }) => ({
          fileName: file.name,
          mimeType: file.type || "application/pdf",
          dataBase64: await toBase64(file),
        })),
      );
      const out = await analyzeBankStatements({ data: { files: payloadFiles } });
      setResult(out);
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const fmt = (n: number, currency: string) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(n);

  const totals = result?.summary.reduce(
    (a, s) => ({
      deposits: a.deposits + s.deposits,
      withdrawals: a.withdrawals + s.withdrawals,
      depositCount: a.depositCount + s.depositCount,
      withdrawalCount: a.withdrawalCount + s.withdrawalCount,
    }),
    { deposits: 0, withdrawals: 0, depositCount: 0, withdrawalCount: 0 },
  );

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl font-bold">Bank Statement Analyzer</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Upload one or more bank statement files (PDF, image, or text). The analyzer extracts
        transactions and returns yearly deposit and withdrawal totals.
      </p>

      <label
        htmlFor="bs-file-input"
        className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-card/40 p-10 text-center transition-colors hover:border-foreground/40 hover:bg-card/70"
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-medium">Click to choose files</p>
        <p className="mt-1 text-xs text-muted-foreground">
          PDF, PNG, JPG, or TXT — up to 12 files
        </p>
        <input
          id="bs-file-input"
          type="file"
          multiple
          accept="application/pdf,image/png,image/jpeg,image/webp,text/plain"
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.currentTarget.value = "";
          }}
        />
      </label>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between rounded-md border border-border bg-card/40 px-3 py-2 text-sm"
            >
              <span className="flex items-center gap-2 truncate">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{f.file.name}</span>
                <span className="text-xs text-muted-foreground">
                  {(f.file.size / 1024).toFixed(0)} KB
                </span>
              </span>
              <button
                onClick={() => removeFile(f.id)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${f.file.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={analyze} disabled={busy || files.length === 0}>
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…
            </>
          ) : (
            "Analyze statements"
          )}
        </Button>
        {files.length > 0 && !busy && (
          <button
            onClick={() => setFiles([])}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-10 space-y-6">
          <div>
            <h2 className="font-display text-xl font-bold">Yearly totals</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Currency: {result.currency}
            </p>
          </div>

          {result.summary.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No transactions could be extracted from the uploaded files.
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="p-3">Year</th>
                    <th className="p-3 text-right">Deposits</th>
                    <th className="p-3 text-right">Withdrawals</th>
                    <th className="p-3 text-right">Net</th>
                    <th className="p-3 text-right">Deposit #</th>
                    <th className="p-3 text-right">Withdrawal #</th>
                  </tr>
                </thead>
                <tbody>
                  {result.summary.map((s) => (
                    <tr key={s.year} className="border-t border-border">
                      <td className="p-3 font-medium">{s.year}</td>
                      <td className="p-3 text-right text-emerald-500">
                        {fmt(s.deposits, result.currency)}
                      </td>
                      <td className="p-3 text-right text-rose-500">
                        {fmt(s.withdrawals, result.currency)}
                      </td>
                      <td
                        className={`p-3 text-right ${s.net >= 0 ? "text-emerald-500" : "text-rose-500"}`}
                      >
                        {fmt(s.net, result.currency)}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {s.depositCount}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {s.withdrawalCount}
                      </td>
                    </tr>
                  ))}
                  {totals && result.summary.length > 1 && (
                    <tr className="border-t border-border bg-muted/30 font-semibold">
                      <td className="p-3">All years</td>
                      <td className="p-3 text-right text-emerald-500">
                        {fmt(totals.deposits, result.currency)}
                      </td>
                      <td className="p-3 text-right text-rose-500">
                        {fmt(totals.withdrawals, result.currency)}
                      </td>
                      <td
                        className={`p-3 text-right ${totals.deposits - totals.withdrawals >= 0 ? "text-emerald-500" : "text-rose-500"}`}
                      >
                        {fmt(totals.deposits - totals.withdrawals, result.currency)}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {totals.depositCount}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {totals.withdrawalCount}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {result.perFile.length > 0 && (
            <div>
              <h3 className="font-semibold">Files processed</h3>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {result.perFile.map((p, i) => (
                  <li key={i}>
                    <span className="text-foreground">{p.fileName}</span>
                    {p.statementPeriod ? ` — ${p.statementPeriod}` : ""}
                    {p.note ? ` · ${p.note}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.notes && (
            <div className="rounded-md border border-border bg-card/40 p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Notes:</span> {result.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
